/** Case→schema selection logic for the Case Schema page (slice 01).
 *
 * A case selects its schema through the `case_settings.schema_id` binding:
 * null ⇒ follow whatever is globally active. These pure helpers resolve which
 * schema a case currently uses (for the "In use" / "Global default" markers) and
 * what value a card selection should persist to the binding. See ADR-0005.
 */

import type { Schema } from '@/services/api/schemas'

type SchemaIdentity = Pick<Schema, 'id' | 'is_active'>

export interface CaseSchemaSelection {
  /** The globally-active schema's id, or null if none is active. */
  globalDefaultId: string | null
  /** The schema the case uses now: the explicit binding, else the global default. */
  inUseId: string | null
}

/** Resolve the global-default and in-use schema ids for a case. */
export function caseSchemaSelection(
  schemas: SchemaIdentity[],
  bindingSchemaId: string | null
): CaseSchemaSelection {
  const globalDefaultId = schemas.find(s => s.is_active)?.id ?? null
  return { globalDefaultId, inUseId: bindingSchemaId ?? globalDefaultId }
}

/**
 * The binding value to persist when the user selects `schemaId`. Selecting the
 * global default clears the binding to null (the case resumes following the
 * global active schema); selecting any other schema pins its id.
 */
export function bindingForSelection(
  schemaId: string,
  globalDefaultId: string | null
): string | null {
  return schemaId === globalDefaultId ? null : schemaId
}

/** Whether a schema is a shared global one or this case's own private copy.
 * In the case-scoped list `case_id == null` ⇒ global; anything else is owned by
 * this case (the list never includes another case's private schemas — SC-006). */
export function schemaOwnership(schema: Pick<Schema, 'case_id'>): 'global' | 'case' {
  return schema.case_id == null ? 'global' : 'case'
}

/** This case's own schema versions (a set `case_id`), newest version first. The
 * case-scoped list never carries another case's private schemas (SC-006), so a
 * set `case_id` ⇒ owned by this case (one schema per case; versions share a name).
 * `versions[0]` is the head/latest. */
export function caseOwnedVersions(schemas: Schema[]): Schema[] {
  return schemas.filter(s => s.case_id != null).sort((a, b) => b.version - a.version)
}
